import { useMemo } from "react";
import { getFlagPayload, useFeatureFlags, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

type Ms = number;
type Sec = number;

type Ttl = { staleTimeMs: Ms };

type CachingConfig = {
  globalQueries: Ttl;
  tenantDirectory: Ttl;
  cameras: { snapTtlSec: Sec };
  alarms: Ttl;
  activity: Ttl;
  clips: { dayTtlMs: Ms };
  livestream: { camTtlMs: Ms; sessTtlMs: Ms };
};

const defaults: CachingConfig = {
  globalQueries: { staleTimeMs: 5 * 60 * 1000 },
  tenantDirectory: { staleTimeMs: 10 * 60 * 1000 },
  cameras: { snapTtlSec: 5 * 60 },
  alarms: { staleTimeMs: 0 },
  activity: { staleTimeMs: 0 },
  clips: { dayTtlMs: 5 * 60 * 1000 },
  livestream: { camTtlMs: 0, sessTtlMs: 30 * 1000 },
};

function mergeConfig(payload: Partial<CachingConfig> | undefined): CachingConfig {
  const p = payload ?? {};
  return {
    globalQueries: {
      staleTimeMs: p.globalQueries?.staleTimeMs ?? defaults.globalQueries.staleTimeMs,
    },
    tenantDirectory: {
      staleTimeMs: p.tenantDirectory?.staleTimeMs ?? defaults.tenantDirectory.staleTimeMs,
    },
    cameras: {
      snapTtlSec: p.cameras?.snapTtlSec ?? defaults.cameras.snapTtlSec,
    },
    alarms: {
      staleTimeMs: p.alarms?.staleTimeMs ?? defaults.alarms.staleTimeMs,
    },
    activity: {
      staleTimeMs: p.activity?.staleTimeMs ?? defaults.activity.staleTimeMs,
    },
    clips: {
      dayTtlMs: p.clips?.dayTtlMs ?? defaults.clips.dayTtlMs,
    },
    livestream: {
      camTtlMs: p.livestream?.camTtlMs ?? defaults.livestream.camTtlMs,
      sessTtlMs: p.livestream?.sessTtlMs ?? defaults.livestream.sessTtlMs,
    },
  };
}

/**
 * Reactive hook — re-renders when the feature flag payload changes.
 * Use in React components / hooks.
 */
export const useCachingConfig = (): CachingConfig => {
  const { getFlagPayload } = useFeatureFlags();
  return useMemo(() => {
    const payload = getFlagPayload<Partial<CachingConfig>>(FEATURE_FLAGS.CACHING_CONFIG);
    return mergeConfig(payload);
  }, [getFlagPayload]);
};

/**
 * Non-reactive getter — reads the current store snapshot.
 * Use in non-React code (Zustand actions, utilities, QueryClient init).
 */
export const getCachingConfig = (): CachingConfig => {
  const payload = getFlagPayload<Partial<CachingConfig>>(FEATURE_FLAGS.CACHING_CONFIG);
  return mergeConfig(payload);
};
